let lastArgsKey = null;
let lastResult = null;

/**
 * Calculate Loan-to-Value (LTV), Combined LTV (CLTV), and High CLTV (HCLTV)
 * @param {string} loanPurpose - Purpose of the loan ("Purchase" or other)
 * @param {number|string} loanAmount - Primary loan amount
 * @param {number|string} purchasePrice - Purchase price of the property
 * @param {number|string} estimatedValue - Estimated/appraised value of the property
 * @param {number|string} closedEndSecondLienAmount - Total of closed-end subordinate liens
 * @param {number|string} heLOCAmount - HELOC draw amount or credit limit
 * @param {number|string} mortgageTotal - Total of other mortgages
 * @returns {Object} - { ltv, cltv, hcltv } each as a string with 3 decimals
 */
export const calculateLtvCltv = ({
  loanPurpose,
  loanAmount,
  purchasePrice,
  estimatedValue,
  closedEndSecondLienAmount = 0,
  heLOCAmount = 0, // Default HELOC amount is set to 0 to ensure safe numeric calculation when no value is provided.
  mortgageTotal = 0,
  helocDrawnAmount,
  helocLineAmount
}) => {

  // Create cache key from inputs
  const currentKey = JSON.stringify({
    loanPurpose,
    loanAmount,
    purchasePrice,
    estimatedValue,
    closedEndSecondLienAmount,
    heLOCAmount,
    mortgageTotal,
    helocDrawnAmount,
    helocLineAmount
  });

  // If inputs haven't changed, return cached result
  if (currentKey === lastArgsKey) {
    return lastResult;
  }

  lastArgsKey = currentKey;

  // Convert all values to numbers safely
  const loanAmountValue = Number(loanAmount) || 0;
  const purchasePriceValue = Number(purchasePrice) || 0;
  const estimatedPropertyValue = Number(estimatedValue) || 0;
  const secondLienValue = Number(closedEndSecondLienAmount) || 0;
  const helocValue = Number(heLOCAmount) || 0;
  const otherMortgageValue = Number(mortgageTotal) || 0;
  const helocDrawnAmountValue = Number(helocDrawnAmount) || 0;
  const helocLineAmountValue = Number(helocLineAmount) || 0;

  let denominator = 0;

  if (loanPurpose?.toLowerCase() === "purchase") {
    // If either value is 0, pick the higher value
    if (purchasePriceValue === 0 || estimatedPropertyValue === 0) {
      denominator = Math.max(purchasePriceValue, estimatedPropertyValue);
      console.log("At least one value is zero, selecting higher value:", denominator);
    } else {
      // Both values > 0, pick the lower value
      denominator = Math.min(purchasePriceValue, estimatedPropertyValue);
      console.log("Both values non-zero, selecting lower value:", denominator);
    }
  } else {
    // For all other loan purposes, use estimated value
    denominator = estimatedPropertyValue;
  }

  // Avoid division by zero
  if (denominator === 0) {
    lastResult = {
      ltv: "0.000",
      cltv: "0.000",
      hcltv: "0.000",
    };
    return lastResult;
  }

  // Calculate ratios
  const ltv = ((loanAmountValue / denominator) * 100).toFixed(3);
  const cltv = (((loanAmountValue + secondLienValue + helocDrawnAmountValue) / denominator) * 100).toFixed(3);
  const hcltv = (((loanAmountValue + secondLienValue + helocLineAmountValue ) / denominator) * 100).toFixed(3);

  lastResult = { ltv, cltv, hcltv };

  return lastResult;
};
